import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useProductDetails } from "@/pages/store-insights/live-experience/live-view/hooks/useProductDetails";
import { EnrichedShopperItem } from "@/pages/store-insights/live-experience/live-view/types";
import { Store } from "@/services/features/StoreService";
import { StateItem } from "@/types/state";
import { formatCurrencyInStore } from "@/utils/currency";

interface ItemCardProps {
  item: StateItem;
  selectedStore: Store | null;
}

const ItemCard: FC<ItemCardProps> = ({ item, selectedStore }) => {
  const { t } = useTranslation();
  const productDetails = useProductDetails(item.sku, selectedStore?.id);

  const formatPrice = (value?: string | null) =>
    value && selectedStore
      ? formatCurrencyInStore(parseFloat(value.replace(/[^0-9.]/g, "") || "0"), selectedStore)
      : undefined;

  const displayItem: EnrichedShopperItem = productDetails
    ? {
        ...item,
        ...productDetails,
        formattedPrice: formatPrice(productDetails.price),
        formattedSalePrice: formatPrice(productDetails.salePrice),
      }
    : item;

  return (
    <Box
      sx={{
        p: 0,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        minHeight: 104,
      }}
    >
      {displayItem.imageUrl && (
        <Box
          component="img"
          src={displayItem.imageUrl}
          alt={displayItem.title || displayItem.sku}
          sx={{
            width: "5.5em",
            minWidth: "5.5em",
            objectFit: "cover",
            display: "block",
            flexShrink: 0,
          }}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 700,
          }}
        >
          {displayItem.title ||
            t("liveView.fittingRooms.shopperDetails.items.skuValue", {
              sku: displayItem.sku,
            })}
        </Typography>

        <Box sx={{ mt: 0.75 }}>
          {(displayItem.color || displayItem.size) && (
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
                mb: 0.75,
              }}
            >
              {displayItem.size && (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    color: "text.secondary",
                  }}
                >
                  {t("liveView.fittingRooms.shopperDetails.items.sizeValue", {
                    size: displayItem.size,
                  })}
                </Typography>
              )}
              {displayItem.color && (
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.25,
                    color: "text.secondary",
                  }}
                >
                  {t("liveView.fittingRooms.shopperDetails.items.colorValue", {
                    color: displayItem.color,
                  })}
                </Typography>
              )}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              pt: 2,
            }}
          >
            <Typography variant="body2" color="text.primary" fontWeight="medium">
              {displayItem.formattedSalePrice ||
                displayItem.formattedPrice ||
                formatCurrencyInStore(
                  parseFloat(
                    (displayItem.salePrice || displayItem.price || "0").replace(/[^0-9.]/g, ""),
                  ),
                  selectedStore,
                )}
            </Typography>
            {displayItem.salePrice && displayItem.price && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textDecoration: "line-through" }}
              >
                {displayItem.formattedPrice ||
                  formatCurrencyInStore(
                    parseFloat((displayItem.price || "0").replace(/[^0-9.]/g, "")),
                    selectedStore,
                  )}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ItemCard;
